import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import * as THREE from 'three';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import type { NetworkPeer } from '../../hooks/useNetworkPeers';

const WIREFRAME_VERT = `
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    vWorldNormal   = normalize((modelMatrix * vec4(normalize(position), 0.0)).xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;
const WIREFRAME_FRAG = `
  uniform vec3 uLightPos;
  uniform vec3 uLightColor;
  uniform vec3 uBaseColor;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  void main() {
    vec3 lightDir = normalize(uLightPos - vWorldPosition);
    float diff = max(dot(vWorldNormal, lightDir), 0.0);
    diff = diff * diff;
    vec3 color = uBaseColor + diff * uLightColor;
    float alpha = 0.3 + diff * 0.45;
    gl_FragColor = vec4(color, alpha);
  }
`;

interface SpinningGlobeProps {
  peers: NetworkPeer[];
}

interface TooltipData {
  x: number;
  y: number;
  city: string;
  country: string;
  block_height: number;
  blocks_behind: number;
  protocol_version: string;
  subversion: string;
  status: string;
  count: number;
}

const STATUS_COLOR_HEX: Record<string, number> = {
  green:  0x00f473,
  yellow: 0xffb000,
  red:    0xff0040,
};

const STATUS_COLOR_CSS: Record<string, string> = {
  green:  '#00f473',
  yellow: '#ffb000',
  red:    '#ff0040',
};

const STATUS_LABEL: Record<string, string> = {
  green:  'Synced',
  yellow: 'Behind',
  red:    'Stale',
};

function latLngToVec3(lat: number, lng: number, r: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
}

export default function SpinningGlobe({ peers }: SpinningGlobeProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const sceneRef = useRef<{
    renderer: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    globe: THREE.LineSegments;
    innerGlobe: THREE.Mesh;
    continentsGroup: THREE.Group;
    markersGroup: THREE.Group;
    animFrameId: number;
  } | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  // Group peers by approximate location
  const { peerGroups, cityCount } = useMemo(() => {
    const groups: Record<string, {
      lat: number; lng: number; color: number; status: string;
      count: number; representative: NetworkPeer;
    }> = {};

    const statusPriority: Record<string, number> = { red: 0, yellow: 1, green: 2 };

    peers.forEach(p => {
      if (!p.lat && !p.lng) return;
      const key = `${Math.round(p.lat * 2) / 2},${Math.round(p.lng * 2) / 2}`;
      const color = STATUS_COLOR_HEX[p.status] ?? STATUS_COLOR_HEX.green;
      if (!groups[key]) {
        groups[key] = { lat: p.lat, lng: p.lng, color, status: p.status, count: 1, representative: p };
      } else {
        groups[key].count++;
        if ((statusPriority[p.status] ?? 2) < (statusPriority[groups[key].status] ?? 2)) {
          groups[key].color = color;
          groups[key].status = p.status;
        }
        // Keep the peer with highest block height as representative
        if (p.block_height > groups[key].representative.block_height) {
          groups[key].representative = p;
        }
      }
    });

    return {
      peerGroups: groups,
      cityCount: Object.keys(groups).length,
    };
  }, [peers]);

  // Raycasting for hover tooltip
  const handleMouseMoveTooltip = useCallback((e: MouseEvent) => {
    const s = sceneRef.current;
    const wrap = wrapRef.current;
    if (!s || !wrap) return;

    const rect = wrap.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, s.camera);
    const hitTargets = s.markersGroup.children.filter(c => c.userData?.isPeer);
    const intersects = raycasterRef.current.intersectObjects(hitTargets);

    if (intersects.length > 0) {
      const data = intersects[0].object.userData;
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        city: data.city || 'Unknown',
        country: data.country || '',
        block_height: data.block_height ?? 0,
        blocks_behind: data.blocks_behind ?? 0,
        protocol_version: data.protocol_version || '',
        subversion: data.subversion || '',
        status: data.status || 'green',
        count: data.count || 1,
      });
    } else {
      setTooltip(null);
    }
  }, []);

  // Init Three.js scene once
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    let mounted = true;

    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.z = 290;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const canvas = renderer.domElement;
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.cursor = 'grab';
    wrap.appendChild(canvas);

    const innerGlobe = new THREE.Mesh(
      new THREE.SphereGeometry(99, 64, 64),
      new THREE.MeshBasicMaterial({ color: 0x020617 }),
    );
    scene.add(innerGlobe);

    const wireframeGeo = new THREE.WireframeGeometry(new THREE.SphereGeometry(100, 40, 40));
    const wireframeMat = new THREE.ShaderMaterial({
      vertexShader: WIREFRAME_VERT,
      fragmentShader: WIREFRAME_FRAG,
      uniforms: {
        uLightPos:   { value: new THREE.Vector3(200, 100, 150) },
        uLightColor: { value: new THREE.Vector3(0, 0.957, 0.451) },
        uBaseColor:  { value: new THREE.Vector3(0.05, 0.145, 0.216) },
      },
      transparent: true,
    });
    const globe = new THREE.LineSegments(wireframeGeo, wireframeMat);
    scene.add(globe);

    const continentsGroup = new THREE.Group();
    scene.add(continentsGroup);
    const continentMaterial = new THREE.LineBasicMaterial({ color: 0x2d6a9f, transparent: true, opacity: 0.9 });

    function drawRing(ring: number[][], group: THREE.Group) {
      if (ring.length < 2) return;
      const points = ring.map(([lng, lat]) => latLngToVec3(lat, lng, 101));
      const geo = new THREE.BufferGeometry().setFromPoints(points);
      group.add(new THREE.Line(geo, continentMaterial));
    }

    fetch('/world-110m.json')
      .then(r => r.json())
      .then((topo: Topology) => {
        if (!mounted) return;
        const obj = topo.objects['countries'] ?? topo.objects['land'] ?? Object.values(topo.objects)[0];
        const geo = feature(topo, obj as Parameters<typeof feature>[1]);
        const features = 'features' in geo ? geo.features : [geo];
        features.forEach(f => {
          if (!f.geometry) return;
          if (f.geometry.type === 'Polygon') {
            f.geometry.coordinates.forEach(ring => drawRing(ring as number[][], continentsGroup));
          } else if (f.geometry.type === 'MultiPolygon') {
            f.geometry.coordinates.forEach(poly =>
              poly.forEach(ring => drawRing(ring as number[][], continentsGroup)),
            );
          }
        });
      })
      .catch(() => {});

    const markersGroup = new THREE.Group();
    scene.add(markersGroup);

    const allGroups = [globe, markersGroup, continentsGroup, innerGlobe];
    const syncRotation = (dx: number, dy: number) => {
      allGroups.forEach(g => {
        g.rotation.y += dx * 0.005;
        g.rotation.x += dy * 0.005;
      });
    };

    let isDragging = false;
    let prevMouse = { x: 0, y: 0 };

    const onMouseDown = () => { isDragging = true; canvas.style.cursor = 'grabbing'; };
    const onMouseUp   = () => { isDragging = false; canvas.style.cursor = 'grab'; };
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      syncRotation(e.offsetX - prevMouse.x, e.offsetY - prevMouse.y);
      prevMouse = { x: e.offsetX, y: e.offsetY };
    };
    const onMouseMovePrev = (e: MouseEvent) => { prevMouse = { x: e.offsetX, y: e.offsetY }; };

    const onTouchStart = (e: TouchEvent) => {
      isDragging = true;
      prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onTouchEnd = () => { isDragging = false; };
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      syncRotation(e.touches[0].clientX - prevMouse.x, e.touches[0].clientY - prevMouse.y);
      prevMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mousemove', onMouseMovePrev);
    canvas.addEventListener('mousemove', handleMouseMoveTooltip);
    canvas.addEventListener('touchstart', onTouchStart);
    canvas.addEventListener('touchend', onTouchEnd);
    canvas.addEventListener('touchmove', onTouchMove);

    const handleResize = () => {
      const nw = wrap.clientWidth;
      const nh = wrap.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    let animFrameId: number;
    function animate() {
      animFrameId = requestAnimationFrame(animate);
      if (!isDragging) {
        allGroups.forEach(g => { g.rotation.y += 0.001; });
      }
      renderer.render(scene, camera);
    }
    animate();

    sceneRef.current = { renderer, camera, globe, innerGlobe, continentsGroup, markersGroup, animFrameId };

    return () => {
      mounted = false;
      cancelAnimationFrame(animFrameId);
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mousemove', onMouseMovePrev);
      canvas.removeEventListener('mousemove', handleMouseMoveTooltip);
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      if (wrap.contains(canvas)) wrap.removeChild(canvas);
      sceneRef.current = null;
    };
  }, [handleMouseMoveTooltip]);

  // Update markers when peer data changes
  useEffect(() => {
    const s = sceneRef.current;
    if (!s) return;
    const { markersGroup } = s;
    while (markersGroup.children.length) markersGroup.remove(markersGroup.children[0]);

    Object.values(peerGroups).forEach(group => {
      const pos = latLngToVec3(group.lat, group.lng, 101);
      const size = Math.min(0.6 + group.count * 0.15, 2.0);
      const color = group.color;

      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(size, 10, 10),
        new THREE.MeshBasicMaterial({ color }),
      );
      marker.position.copy(pos);
      marker.userData = {
        isPeer: true,
        city: group.representative.city,
        country: group.representative.country,
        block_height: group.representative.block_height,
        blocks_behind: group.representative.blocks_behind,
        protocol_version: group.representative.protocol_version,
        subversion: group.representative.subversion,
        status: group.status,
        count: group.count,
      };
      markersGroup.add(marker);

      // Glow ring for clusters
      if (group.count > 1) {
        const ringSize = Math.min(2.5 + group.count * 0.3, 5);
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(ringSize, ringSize + 0.8, 16),
          new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.2, side: THREE.DoubleSide }),
        );
        ring.position.copy(pos);
        ring.lookAt(new THREE.Vector3(0, 0, 0));
        markersGroup.add(ring);
      }
    });
  }, [peerGroups]);

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        background: 'rgba(15,23,42,0.3)',
        borderRadius: 40,
        overflow: 'hidden',
        minHeight: 500,
      }}
    >
      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 12,
            top: tooltip.y - 10,
            zIndex: 20,
            background: 'rgba(15,23,42,0.95)',
            border: `1px solid ${STATUS_COLOR_CSS[tooltip.status] ?? '#00f473'}`,
            borderRadius: 8,
            padding: '8px 12px',
            pointerEvents: 'none',
            fontSize: 12,
            color: '#e2e8f0',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
          }}
        >
          <div style={{ fontWeight: 700, color: STATUS_COLOR_CSS[tooltip.status] ?? '#00f473', marginBottom: 4 }}>
            {tooltip.city}{tooltip.country ? `, ${tooltip.country}` : ''}
            {tooltip.count > 1 && <span style={{ color: '#94a3b8' }}> ({tooltip.count} peers)</span>}
          </div>
          <div>Block: {tooltip.block_height.toLocaleString()}</div>
          {tooltip.blocks_behind > 0 && (
            <div style={{ color: STATUS_COLOR_CSS[tooltip.status] }}>
              {tooltip.blocks_behind.toLocaleString()} blocks behind
            </div>
          )}
          {tooltip.subversion && <div>Wallet: {tooltip.subversion}</div>}
          {!tooltip.subversion && tooltip.protocol_version && (
            <div>Protocol: {tooltip.protocol_version}</div>
          )}
        </div>
      )}

      {/* Color legend */}
      {peers.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          zIndex: 10,
          pointerEvents: 'none',
          fontSize: 10,
          color: '#94a3b8',
          fontFamily: 'monospace',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#00f473', display: 'inline-block' }} />
            Synced (within 10 blocks)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ffb000', display: 'inline-block' }} />
            Behind (11–5,000 blocks)
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff0040', display: 'inline-block' }} />
            Stale (5,000+ blocks)
          </div>
        </div>
      )}

      {/* Stats overlay */}
      <div style={{
        position: 'absolute',
        bottom: 24,
        right: 24,
        textAlign: 'right',
        zIndex: 10,
        pointerEvents: 'none',
      }}>
        <p style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 2 }}>
          Live Network
        </p>
        <p style={{ fontSize: 24, fontWeight: 900, color: '#fff' }}>
          {peers.length} Peers | {cityCount} Locations
        </p>
      </div>
    </div>
  );
}
